/**
 * Leviathan Web - Dashboard Layout Component
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Main application layout with header, sidebar, content area, and status footer
 */

import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Wifi, WifiOff, Database, Server, Activity } from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import RouteErrorBoundary from '@components/RouteErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';

import { API_URL } from '@/config/api';

interface SystemStatus {
  api: 'online' | 'offline' | 'checking';
  database: 'online' | 'offline' | 'checking';
  lastCheck: Date | null;
}

/**
 * Dashboard layout wrapper with tactical industrial styling
 */
const DashboardLayout = () => {
  const { accessToken } = useAuth();
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    api: 'checking',
    database: 'checking',
    lastCheck: null,
  });

  // Check system status periodically
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/health`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });

        if (response.ok) {
          const data = await response.json();
          setSystemStatus({
            api: 'online',
            database: data.database === 'connected' ? 'online' : 'offline',
            lastCheck: new Date(),
          });
        } else {
          setSystemStatus(prev => ({
            ...prev,
            api: 'offline',
            lastCheck: new Date(),
          }));
        }
      } catch {
        setSystemStatus({
          api: 'offline',
          database: 'offline',
          lastCheck: new Date(),
        });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [accessToken]);

  const getStatusColor = (status: 'online' | 'offline' | 'checking') => {
    switch (status) {
      case 'online':
        return 'text-success-green';
      case 'offline':
        return 'text-error-red';
      case 'checking':
        return 'text-amber';
    }
  };

  const getStatusGlow = (status: 'online' | 'offline' | 'checking') => {
    switch (status) {
      case 'online':
        return '0 0 6px rgba(74,222,128,0.8)';
      case 'offline':
        return '0 0 6px rgba(239,68,68,0.8)';
      case 'checking':
        return '0 0 6px rgba(255,149,0,0.6)';
    }
  };

  return (
    <div className="min-h-screen bg-base-dark bg-grid-pattern flex flex-col">
      {/* Header */}
      <Header />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar />

        {/* Main content area.
            The boundary sits INSIDE the layout on purpose: a screen that
            throws leaves the header and sidebar standing, so the operator can
            navigate away instead of staring at a white page. */}
        <main className="flex-1 p-6 overflow-y-auto">
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>

      {/* Footer Status Bar */}
      <footer className="h-8 bg-panel-dark border-t border-panel-border flex items-center px-4 justify-between relative">
        {/* Corner decorations */}
        <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber/30" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber/30" />

        {/* Left section - System status */}
        <div className="flex items-center space-x-4">
          {/* API Status */}
          <div className="flex items-center space-x-1.5">
            <div
              className={`w-1.5 h-1.5 rounded-full ${systemStatus.api === 'online' ? 'bg-success-green' : systemStatus.api === 'offline' ? 'bg-error-red' : 'bg-amber'} ${systemStatus.api === 'checking' ? 'animate-pulse' : ''}`}
              style={{ boxShadow: getStatusGlow(systemStatus.api) }}
            />
            <Server className={`w-3 h-3 ${getStatusColor(systemStatus.api)}`} />
            <span className="text-[10px] font-mono text-text-tertiary uppercase tracking-wider">
              API
            </span>
          </div>

          {/* Database Status */}
          <div className="flex items-center space-x-1.5">
            <div
              className={`w-1.5 h-1.5 rounded-full ${systemStatus.database === 'online' ? 'bg-success-green' : systemStatus.database === 'offline' ? 'bg-error-red' : 'bg-amber'} ${systemStatus.database === 'checking' ? 'animate-pulse' : ''}`}
              style={{ boxShadow: getStatusGlow(systemStatus.database) }}
            />
            <Database className={`w-3 h-3 ${getStatusColor(systemStatus.database)}`} />
            <span className="text-[10px] font-mono text-text-tertiary uppercase tracking-wider">
              DB
            </span>
          </div>

          {/* Connection indicator */}
          <div className="flex items-center space-x-1.5 pl-2 border-l border-panel-border">
            {navigator.onLine ? (
              <Wifi className="w-3 h-3 text-success-green" />
            ) : (
              <WifiOff className="w-3 h-3 text-error-red" />
            )}
            <span className="text-[10px] font-mono text-text-tertiary uppercase tracking-wider">
              {navigator.onLine ? 'Connected' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Center section - Activity indicator */}
        <div className="flex items-center space-x-2">
          <Activity className="w-3 h-3 text-amber" />
          <span className="text-[10px] font-mono text-text-tertiary">
            {systemStatus.lastCheck
              ? `Last sync: ${systemStatus.lastCheck.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
              : 'Syncing...'}
          </span>
        </div>

        {/* Right section - Version info */}
        <div className="flex items-center space-x-3">
          <span className="text-[10px] font-mono text-text-tertiary uppercase tracking-wider">
            Leviathan v0.1.0
          </span>
          <div className="h-3 w-px bg-panel-border" />
          <span className="text-[10px] font-mono text-text-tertiary">
            Quasar Prime LLC
          </span>
        </div>
      </footer>
    </div>
  );
};

export default DashboardLayout;
